import React from "react";
import {  Info, BookOpen, ListChecks,  Utensils } from "lucide-react";

const HostelInfo = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 bg-white/70 backdrop-blur-lg shadow-xl rounded-2xl border border-gray-200 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">🏨 NIGST Hostel</h1>
        <p className="text-gray-600">General Information & Booking Guidelines</p>
      </div>

      {/* General Information */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Info className="text-blue-600" />
          <h2 className="text-2xl font-semibold text-gray-800">General Information</h2>
        </div>
        <p className="text-gray-700 leading-relaxed">
          Requests for booking accommodation at NIGST-Hostel should be invariably submitted
          through email:{" "}
          <a
            href="mailto:hostel.nigst@surveyofindia.gov.in"
            className="text-blue-600 underline"
          >
            hostel.nigst@surveyofindia.gov.in
          </a>
          , well in advance with complete details. Accommodation and food charges are billed
          separately and are to be settled as per prescribed rules. All bookings are subject
          to availability and prior approval by competent authority.
        </p>
      </section>

      {/* Booking Guidelines */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="text-green-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Hostel Booking Guidelines</h2>
        </div>
        <ul className="list-disc ml-6 text-gray-700 space-y-2">
          <li>
            Requests should be submitted through email to:{" "}
            <a
              href="mailto:hostel.nigst@surveyofindia.gov.in"
              className="text-blue-600 underline"
            >
              hostel.nigst@surveyofindia.gov.in
            </a>
          </li>
          <li>Include guest details, duration of stay, and purpose of visit.</li>
          <li>
            Management reserves the right to cancel, modify, or provide shared accommodation
            based on availability.
          </li>
          <li>Any change in arrival/departure must be communicated immediately.</li>
        </ul>
      </section>

      {/* Charges Table */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="text-yellow-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Accommodation Charges</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border border-gray-300 rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr className="text-left">
                <th className="py-2 px-4 border-b">SI No.</th>
                <th className="py-2 px-4 border-b">Category</th>
                <th className="py-2 px-4 border-b">Charges per day per person</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "Trainees Staying for all Courses", "₹ 325.00"],
                ["2", "SOI officials on Government duty", "₹ 375.00"],
                ["3", "Government officials other than SOI", "₹ 475.00"],
                ["4", "Others", "₹ 525.00"],
              ].map(([id, category, charge]) => (
                <tr key={id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4">{id}</td>
                  <td className="py-2 px-4">{category}</td>
                  <td className="py-2 px-4">{charge}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-600 text-sm mt-2">
          <strong>Note:</strong> Check-out time will be before 18:00 hrs.
        </p>
      </section>

      {/* Hostel Rules */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Info className="text-purple-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Hostel Rules & Regulations</h2>
        </div>

        <h3 className="font-semibold text-lg mt-2 text-gray-700">✅ Do’s</h3>
        <ul className="list-decimal ml-6 text-gray-700 space-y-1">
          <li>Use electricity and water judiciously.</li>
          <li>Maintain cleanliness in rooms, bathrooms, and common areas.</li>
          <li>Verify room inventory during check-in and check-out.</li>
          <li>Maintain discipline and decorum at all times.</li>
          <li>Report suspicious activity to the Warden.</li>
          <li>Pay dues on time via BharatKosh portal.</li>
          <li>Deposit room keys when vacating during off-hours.</li>
        </ul>

        <h3 className="font-semibold text-lg mt-4 text-gray-700">🚫 Don’ts</h3>
        <ul className="list-decimal ml-6 text-gray-700 space-y-1">
          <li>Cooking inside hostel rooms is strictly prohibited.</li>
          <li>Smoking, alcohol, and intoxicants are banned.</li>
          <li>No disturbance or inappropriate behaviour allowed.</li>
        </ul>

        <p className="text-red-600 text-sm mt-2">
          Violation of rules may lead to cancellation of accommodation.
        </p>
      </section>

      {/* Mess Rules */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Utensils className="text-orange-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Mess Rules & Timings</h2>
        </div>

        <ul className="list-decimal ml-6 text-gray-700 space-y-1">
          <li>Mess subscription must be paid in advance.</li>
          <li>Maintain discipline and decorum.</li>
          <li>Entry restricted to authorized trainees and staff.</li>
          <li>Avoid wastage of food and maintain cleanliness.</li>
          <li>Keep polite conversation; no loud talking or speaker use.</li>
          <li>Report disputes to the Mess Committee or Warden.</li>
          <li>Follow mess timings strictly.</li>
        </ul>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full border border-gray-300 rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr className="text-left">
                <th className="py-2 px-4 border-b">Meal</th>
                <th className="py-2 px-4 border-b">Weekdays</th>
                <th className="py-2 px-4 border-b">Weekends / Holidays</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Breakfast", "07:00 – 08:30 AM", "07:30 – 09:00 AM"],
                ["Lunch", "12:30 – 02:00 PM", "01:00 – 02:30 PM"],
                ["Evening Tea", "04:30 – 05:30 PM", "05:00 – 06:00 PM"],
                ["Dinner", "07:30 – 09:30 PM", "08:00 – 09:30 PM"],
              ].map(([meal, weekday, weekend]) => (
                <tr key={meal} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4">{meal}</td>
                  <td className="py-2 px-4">{weekday}</td>
                  <td className="py-2 px-4">{weekend}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default HostelInfo;
